import { ApiRequestError } from "@nexxt/api-client";
import { RouteError } from "@nexxt/app-kit";
import { attributeQueries, columnSets, lineQueries, linesheetQueries, lineViews, skuViews } from "@nexxt/catalog-ui";
import { seasonCode, seasonDeadlineDomains } from "@nexxt/contracts";
import { listAppointmentsInput, listSalesCampaignsInput } from "@nexxt/contracts/booking";
import {
  listAttributeCompletenessInput,
  listAttributeTasksInput,
  listLinesheetInvitationsInput,
  listLinesheetsInput,
  listProductsInput,
  listStyleMarginsInput,
} from "@nexxt/contracts/catalog";
import { listClaimsInput } from "@nexxt/contracts/claims";
import { listDocumentsInput } from "@nexxt/contracts/documents";
import {
  connectClaudePath,
  listDelegatedAccessInput,
  myTokensPath,
  preparedActionsPath,
  signInPageInput,
} from "@nexxt/contracts/identity";
import {
  erpFileKinds,
  erpFileStatuses,
  listIntegrationErrorsInput,
  listReconciliationIssuesInput,
} from "@nexxt/contracts/integration";
import {
  listAvailabilityInput,
  listAvailabilityMismatchesInput,
  listFulfillmentsInput,
  listStockAdjustmentsInput,
  listStockMovementsInput,
  listVendorReturnsInput,
  listWaitingAvailabilityInput,
  listWaitingDifferencesInput,
  listWaitingMovementExecutionsInput,
  listWaitingShipmentsInput,
} from "@nexxt/contracts/logistics";
import { listEmailSuppressionsInput, listNotificationsInput } from "@nexxt/contracts/notifications";
import { parameterKeys } from "@nexxt/contracts/parameters";
import {
  bcRecordKinds,
  hubspotRecordKinds,
  hubspotWaitingReasons,
  listBcRecordsInput,
  listBuyingGroupsInput,
  listEtafApplicationsInput,
  listHubspotRecordsInput,
  listManufacturersInput,
  listOrganizationsInput,
  listUsersInput,
} from "@nexxt/contracts/party";
import {
  listCreditNotesInput,
  listDemandRunsInput,
  listInboundProgressInput,
  listInboundsInput,
  listPurchaseOrdersInput,
  listSupplierPurchaseTotalsInput,
  listWaitingReceiptsInput,
  poChangeStatuses,
  poLineStatuses,
} from "@nexxt/contracts/procurement";
import {
  listCodeMappingsInput,
  listReferenceRecordsInput,
  listWaitingGatesInput,
  referencedDatasets,
} from "@nexxt/contracts/reference";
import {
  cashProjectionBuckets,
  cashProjectionExclusions,
  getCashProjectionInput,
  listAllocationOrdersInput,
  listBlockedCustomersInput,
  listCreditDecisionsInput,
  listJoorOrdersInput,
  listSalesOrdersInput,
  listSalesOrderTotalsInput,
  listShortShipmentsInput,
  soLineStatuses,
} from "@nexxt/contracts/sales";
import { vendorReturnQueries } from "@nexxt/logistics-ui";
import { userQueries } from "@nexxt/party-ui";
import {
  creditNoteQueries,
  inboundQueries,
  lineQueries as poLineQueries,
  purchaseOrderQueries,
} from "@nexxt/procurement-ui";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  notFound,
  redirect,
} from "@tanstack/react-router";
import * as z from "zod";
import { queryClient } from "./api";
import { allocationScheduleQueries } from "./features/allocation-schedule/allocation-schedule.queries";
import { allocationSettingQueries } from "./features/allocation-setting/allocation-setting.queries";
import { appointmentQueries } from "./features/appointment/appointment.queries";
import { meQuery } from "./features/auth/me";
import { bcRecordQueries } from "./features/bc-record/bc-record.queries";
import { blockedCustomerQueries } from "./features/blocked-customer/blocked-customer.queries";
import { bookingQueries } from "./features/booking/booking.queries";
import { availabilityQueries } from "./features/brand-availability/availability.queries";
import { buyingGroupQueries } from "./features/buying-group/buying-group.queries";
import { cashProjectionQueries } from "./features/cash-projection/cash-projection.queries";
import { claimQueries } from "./features/claim/claim.queries";
import { codeMappingQueries, DEFAULT_DOMAIN, DEFAULT_SYSTEM } from "./features/code-mapping/code-mapping.queries";
import { countryAreaManagerQueries } from "./features/country-area-manager/country-area-manager.queries";
import { creditQueries } from "./features/credit/credit.queries";
import { delegatedAccessQueries } from "./features/delegated-access/delegated-access.queries";
import { demandQueries } from "./features/demand/demand.queries";
import { documentQueries } from "./features/document/document.queries";
import { suppressionQueries } from "./features/email-suppression/suppression.queries";
import { erpFileQueries } from "./features/erp-file/erp-file.queries";
import { etafQueries, etafViews } from "./features/etaf/etaf.queries";
import { fulfillmentQueries, fulfillmentTabs } from "./features/fulfillment/fulfillment.queries";
import { gateQueries } from "./features/gate/gate.queries";
import { waitingGateQueries } from "./features/gate/waiting-gate.queries";
import { HomePage } from "./features/home/home-page";
import { hubspotRecordQueries } from "./features/hubspot-record/hubspot-record.queries";
import { inboundTabs } from "./features/inbound/inbound-tabs";
import { inboundProgressQueries } from "./features/inbound-progress/inbound-progress.queries";
import { integrationQueries, reconciliationQueries } from "./features/integration/integration.queries";
import { joorOrderQueries } from "./features/joor-order/joor-order.queries";
import { invitationQueries } from "./features/linesheet-invitation/invitation.queries";
import { manufacturerQueries } from "./features/manufacturer/manufacturer.queries";
import { movementExecutionQueries } from "./features/movement-execution/movement-execution.queries";
import { notificationQueries } from "./features/notification/notification.queries";
import { preferenceQueries } from "./features/notification-preference/preference.queries";
import { templateQueries } from "./features/notification-template/template.queries";
import { organizationQueries, organizationTabs } from "./features/organization/organization.queries";
import { parameterQueries } from "./features/parameter/parameter.queries";
import { preparedActionQueries } from "./features/prepared-action/prepared-action.queries";
import { priceSetupQueries } from "./features/price-setup/price-setup.queries";
import { setupScreenTabs } from "./features/price-setup/price-setup-form";
import { priceViews } from "./features/pricing/price-grid";
import { productQueries } from "./features/product/product.queries";
import { purchaseOrderTabs } from "./features/purchase-order/purchase-order-tabs";
import { receiptQueries } from "./features/receipt/receipt.queries";
import { recipientListQueries } from "./features/recipient-list/recipient-list.queries";
import { DEFAULT_DATASET, referenceQueries } from "./features/reference/reference.queries";
import { roleQueries, roleTabs } from "./features/role/role.queries";
import { campaignQueries } from "./features/sales-campaign/campaign.queries";
import { salesOrderQueries, salesOrderTabs } from "./features/sales-order/sales-order.queries";
import { soLineQueries } from "./features/sales-order-line/line.queries";
import { salesOrderTotalQueries } from "./features/sales-order-total/sales-order-total.queries";
import { typeOrder } from "./features/search/search-types";
import { shipmentQueries } from "./features/shipment/shipment.queries";
import { shortShipmentQueries } from "./features/short-shipment/short-shipment.queries";
import { stockAdjustmentQueries } from "./features/stock-adjustment/stock-adjustment.queries";
import { stockAvailabilityQueries } from "./features/stock-availability/stock-availability.queries";
import { stockMovementQueries, stockMovementTabs } from "./features/stock-movement/stock-movement.queries";
import { toleranceBandQueries } from "./features/tolerance-band/tolerance-band.queries";
import { vendorReturnTabs } from "./features/vendor-return/vendor-return.queries";
import { AppShell } from "./shell/app-shell";

// Routes (architettura-frontend.md): navigation state lives in the typed search params, and every loader preloads
// its queries (ensureQueryData), so screens render without request waterfalls.

// ADR-302: "To do", the rows of the queues of the place, in the search of each list that has it. The queues are the
// user's, which the page reads, so a loader does not preload the list filtered "To do".
const todoSearch = { todo: z.literal(true).optional().catch(undefined) };

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()();

// Where the gateway sends a failed login (P2.7): public, outside the shell, and without the user query, so a refused
// account is not sent back to the login in a loop.
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: signInPageInput,
  component: lazyRouteComponent(() => import("./features/auth/sign-in-page"), "SignInPage"),
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_shell",
  // every screen needs the user; a 401 sends the browser to the login (api.ts)
  beforeLoad: ({ context }) => context.queryClient.ensureQueryData(meQuery),
  component: AppShell,
  errorComponent: RouteError,
});

const homeRoute = createRoute({ getParentRoute: () => shellRoute, path: "/", component: HomePage });

// the search across the domains (ADR-117): no loader, the domains are asked as the page renders, each on its own
const searchRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/search",
  validateSearch: z.object({
    q: z.string().trim().min(1).max(100).optional().catch(undefined),
    type: z.enum(typeOrder).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/search/search-page"), "SearchPage"),
});

const linesheetsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/linesheets",
  // the filters follow the rules of the list input; a malformed value is dropped, not an error page
  validateSearch: z.object({
    status: listLinesheetsInput.shape.status.catch(undefined),
    q: listLinesheetsInput.shape.q.catch(undefined),
    priceStatus: listLinesheetsInput.shape.priceStatus.catch(undefined),
    bcSyncStatus: listLinesheetsInput.shape.bcSyncStatus.catch(undefined),
    joorSyncStatus: listLinesheetsInput.shape.joorSyncStatus.catch(undefined),
    archived: z.boolean().optional().catch(undefined),
    pricesStale: z.boolean().optional().catch(undefined),
    // one brand, as the Activity of its organization opens its linesheets (ADR-322)
    brand: listLinesheetsInput.shape.brand.catch(undefined),
    ...todoSearch,
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({
    status: search.status,
    priceStatus: search.priceStatus,
    bcSyncStatus: search.bcSyncStatus,
    joorSyncStatus: search.joorSyncStatus,
    q: search.q,
    archived: search.archived,
    pricesStale: search.pricesStale,
    brand: search.brand,
    todo: search.todo,
  }),
  loader: async ({ context, deps: { todo, ...filters } }) => {
    if (!todo) await context.queryClient.ensureInfiniteQueryData(linesheetQueries.list(filters));
  },
  // code split by route: the grid (TanStack Table and Virtual) loads with the first linesheet screen
  component: lazyRouteComponent(() => import("./features/linesheet/linesheets-page"), "LinesheetsPage"),
});

const linesheetRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/linesheets/$linesheetId",
  validateSearch: z.object({
    // the ERP tab: the status of the SKUs towards the ERP and the files of the items (ADR-257, ADR-258)
    tab: z.enum(["lines", "prices", "sync", "erp", "history"]).optional().catch(undefined),
    show: z.enum(lineViews).optional().catch(undefined),
    lq: z.string().trim().min(1).max(100).optional().catch(undefined),
    // the line open in the panel, or the form of a new one
    line: z
      .union([z.uuid(), z.literal("new")])
      .optional()
      .catch(undefined),
    // the price list and currency on display in the Prices tab (`EXW-IT/EUR`), and which lines it shows
    pl: z.string().trim().min(1).max(110).optional().catch(undefined),
    pv: z.enum(priceViews).optional().catch(undefined),
    // the sync run open in the panel of the Sync tab
    run: z.uuid().optional().catch(undefined),
    // the panel of the assistant (ADR-284)
    ai: z.boolean().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ view: search.tab === "prices" ? undefined : search.show, q: search.lq }),
  loader: async ({ context, params, deps }) => {
    const ls = await context.queryClient.ensureQueryData(linesheetQueries.detail(params.linesheetId));
    // the lines are read by id, the route also answers to a name
    await context.queryClient.ensureInfiniteQueryData(lineQueries.list(ls.id, deps));
  },
  component: lazyRouteComponent(() => import("./features/linesheet/linesheet-page"), "LinesheetPage"),
});

const productsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/products",
  validateSearch: z.object({
    status: listProductsInput.shape.status.catch(undefined),
    q: listProductsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(productQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/product/products-page"), "ProductsPage"),
});

const priceSetupRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/pricing/setup",
  validateSearch: z.object({
    season: z.string().trim().min(1).max(100).optional().catch(undefined),
    tab: z.enum(setupScreenTabs).optional().catch(undefined),
    brand: z.uuid().optional().catch(undefined),
    // the price list open in the panel
    list: z.string().trim().min(1).max(100).optional().catch(undefined),
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(priceSetupQueries.options()),
  component: lazyRouteComponent(() => import("./features/price-setup/price-setup-page"), "PriceSetupPage"),
});

// B-59 (ADR-215): the estimated and the actual margin of a season, per style
const marginsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/margins",
  validateSearch: z.object({
    season: listStyleMarginsInput.shape.season.optional().catch(undefined),
    brand: listStyleMarginsInput.shape.brand.catch(undefined),
    q: listStyleMarginsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(priceSetupQueries.options()),
  component: lazyRouteComponent(() => import("./features/margin/margins-page"), "MarginsPage"),
});

const attributeTasksRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/attributes",
  validateSearch: z.object({
    status: listAttributeTasksInput.shape.status.catch(undefined),
    attributesStatus: listAttributeTasksInput.shape.attributesStatus.catch(undefined),
    brand: listAttributeTasksInput.shape.brand.catch(undefined),
    q: listAttributeTasksInput.shape.q.catch(undefined),
    sort: listAttributeTasksInput.shape.sort.unwrap().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(attributeQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/attributes/attribute-tasks-page"), "AttributeTasksPage"),
});

// spec v0.2 §9, P8.4b: the attributes of each brand, the nearest due date first
const attributeCompletenessRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/attribute-completeness",
  validateSearch: z.object({ season: listAttributeCompletenessInput.shape.season.catch(undefined) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(attributeQueries.completeness(deps)),
  component: lazyRouteComponent(
    () => import("./features/attributes/attribute-completeness-page"),
    "AttributeCompletenessPage",
  ),
});

// ADR-325: the deadlines of a season across the domains, a lane per domain and an agenda by week
const seasonCalendarRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/season-calendar",
  validateSearch: z.object({
    season: seasonCode.optional().catch(undefined),
    brand: z.uuid().optional().catch(undefined),
    domain: z.enum(seasonDeadlineDomains).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/season-calendar/season-calendar-page"), "SeasonCalendarPage"),
});

// ADR-327: what will stop the syncs of a season, by cause, the nearest deadline first
const seasonBlockersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/season-blockers",
  validateSearch: z.object({ season: seasonCode.optional().catch(undefined) }),
  component: lazyRouteComponent(() => import("./features/season-blockers/season-blockers-page"), "SeasonBlockersPage"),
});

// ADR-326: the scorecard of brands and suppliers by season, a tab per side
const scorecardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/scorecard",
  validateSearch: z.object({
    season: seasonCode.optional().catch(undefined),
    side: z.enum(["brand", "supplier"]).optional().catch(undefined),
  }),
  component: lazyRouteComponent(() => import("./features/scorecard/scorecard-page"), "ScorecardPage"),
});

const attributeTaskRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/attributes/$taskId",
  validateSearch: z.object({
    // the group of columns on display, which SKUs, and the search
    cols: z.enum(columnSets).optional().catch(undefined),
    show: z.enum(skuViews).optional().catch(undefined),
    q: z.string().trim().min(1).max(100).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ view: search.show, q: search.q }),
  loader: ({ context, params, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(attributeQueries.detail(params.taskId)),
      context.queryClient.ensureInfiniteQueryData(attributeQueries.skus(params.taskId, deps)),
      context.queryClient.ensureQueryData(attributeQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/attributes/attribute-task-page"), "AttributeTaskPage"),
});

const invitationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/linesheet-invitations",
  validateSearch: z.object({
    season: listLinesheetInvitationsInput.shape.season.catch(undefined),
    campaign: listLinesheetInvitationsInput.shape.salesCampaignId.catch(undefined),
    // B-97, ADR-245: the brands still waited for after the reminders, Operations' queue
    remindersOver: z.boolean().optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({
    season: search.season,
    salesCampaignId: search.campaign,
    remindersOver: search.remindersOver,
  }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(invitationQueries.list(deps)),
      context.queryClient.ensureQueryData(linesheetQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/linesheet-invitation/invitations-page"), "InvitationsPage"),
});

const salesCampaignsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/sales-campaigns",
  validateSearch: z.object({
    q: listSalesCampaignsInput.shape.q.catch(undefined),
    season: listSalesCampaignsInput.shape.season.catch(undefined),
    archived: z.boolean().optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ q: search.q, season: search.season, archived: search.archived }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(campaignQueries.list(deps)),
      context.queryClient.ensureQueryData(bookingQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/sales-campaign/campaigns-page"), "CampaignsPage"),
});

const salesCampaignRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/sales-campaigns/$salesCampaignId",
  validateSearch: z.object({
    tab: z.enum(["availabilities", "appointments"]).optional().catch(undefined),
    // the appointment open in the panel of the Appointments tab
    panel: z.uuid().optional().catch(undefined),
  }),
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(campaignQueries.detail(params.salesCampaignId)),
      context.queryClient.ensureInfiniteQueryData(availabilityQueries.list(params.salesCampaignId)),
    ]),
  component: lazyRouteComponent(() => import("./features/sales-campaign/campaign-page"), "CampaignPage"),
});

const appointmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/appointments",
  validateSearch: z.object({
    campaign: listAppointmentsInput.shape.salesCampaignId.catch(undefined),
    brand: listAppointmentsInput.shape.brandOrgId.catch(undefined),
    customer: listAppointmentsInput.shape.customerOrgId.catch(undefined),
    status: listAppointmentsInput.shape.status.catch(undefined),
    from: listAppointmentsInput.shape.from.catch(undefined),
    to: listAppointmentsInput.shape.to.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({
    salesCampaignId: search.campaign,
    brandOrgId: search.brand,
    customerOrgId: search.customer,
    status: search.status,
    from: search.from,
    to: search.to,
  }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(appointmentQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/appointment/appointments-page"), "AppointmentsPage"),
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/purchase-orders",
  validateSearch: z.object({
    status: listPurchaseOrdersInput.shape.status.catch(undefined),
    brand: listPurchaseOrdersInput.shape.brand.catch(undefined),
    supplier: listPurchaseOrdersInput.shape.supplier.catch(undefined),
    season: listPurchaseOrdersInput.shape.season.catch(undefined),
    destination: listPurchaseOrdersInput.shape.destination.catch(undefined),
    bcSyncStatus: listPurchaseOrdersInput.shape.bcSyncStatus.catch(undefined),
    pendingChanges: z.boolean().optional().catch(undefined),
    // confirmed, with units no inbound announces yet: the documents the supplier has to upload (ADR-170)
    toAnnounce: z.boolean().optional().catch(undefined),
    // B-37, ADR-245: not confirmed after the last reminder, Operations' queue
    remindersOver: z.boolean().optional().catch(undefined),
    // the queues of the supplier's side open the list with it: for Tomorrow it takes nothing out (ADR-168)
    asSupplier: z.boolean().optional().catch(undefined),
    q: listPurchaseOrdersInput.shape.q.catch(undefined),
    ...todoSearch,
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps: { todo, ...filters } }) =>
    Promise.all([
      todo || context.queryClient.ensureInfiniteQueryData(purchaseOrderQueries.list(filters)),
      context.queryClient.ensureQueryData(purchaseOrderQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/purchase-order/purchase-orders-page"), "PurchaseOrdersPage"),
});

const purchaseOrderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/purchase-orders/$purchaseOrderId",
  validateSearch: z.object({
    tab: z.enum(purchaseOrderTabs).optional().catch(undefined),
    // which lines the grid shows, and which proposals
    show: z.enum(poLineStatuses).optional().catch(undefined),
    cs: z.enum(poChangeStatuses).optional().catch(undefined),
    // the revision open in the panel of the Revisions tab
    rev: z.number().int().min(1).optional().catch(undefined),
    // the panel of the assistant (ADR-284)
    ai: z.boolean().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ status: search.show }),
  loader: async ({ context, params, deps }) => {
    const po = await context.queryClient.ensureQueryData(purchaseOrderQueries.detail(params.purchaseOrderId));
    // the lines are read by id, the route also answers to a number
    await context.queryClient.ensureInfiniteQueryData(poLineQueries.list(po.id, deps.status));
  },
  component: lazyRouteComponent(() => import("./features/purchase-order/purchase-order-page"), "PurchaseOrderPage"),
});

// The demand of the purchase orders (ADR-143) and the POs counted by supplier (v0.2 §9).
const demandRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/demand",
  validateSearch: z.object({
    season: listDemandRunsInput.shape.season.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(demandQueries.runs(deps)),
      context.queryClient.ensureQueryData(purchaseOrderQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/demand/demand-page"), "DemandPage"),
});

const supplierTotalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/supplier-purchase-totals",
  validateSearch: z.object({
    season: listSupplierPurchaseTotalsInput.shape.season.catch(undefined),
    supplier: listSupplierPurchaseTotalsInput.shape.supplier.catch(undefined),
    status: listSupplierPurchaseTotalsInput.shape.status.catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(demandQueries.supplierTotals(deps)),
      context.queryClient.ensureQueryData(purchaseOrderQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/demand/supplier-totals-page"), "SupplierTotalsPage"),
});

// spec v0.2 §9, P8.10a: the POs coming in, received against expected, with their open discrepancies
const inboundProgressRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/inbound-progress",
  validateSearch: z.object({
    season: listInboundProgressInput.shape.season.catch(undefined),
    supplier: listInboundProgressInput.shape.supplier.catch(undefined),
    brand: listInboundProgressInput.shape.brand.catch(undefined),
    stage: listInboundProgressInput.shape.stage.catch(undefined),
    discrepancies: z.boolean().optional().catch(undefined),
    q: listInboundProgressInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  // the values of the filters come with the page: a role that reads the inbounds and not the POs still sees the view
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(inboundProgressQueries.list(deps)),
  component: lazyRouteComponent(
    () => import("./features/inbound-progress/inbound-progress-page"),
    "InboundProgressPage",
  ),
});

const inboundsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/inbounds",
  validateSearch: z.object({
    status: listInboundsInput.shape.status.catch(undefined),
    // what waits for its first packing list is two statuses (ADR-132): the status filter offers it, as the queue counts it
    toCapture: z.boolean().optional().catch(undefined),
    // ADR-233: the lines the extraction tool read wait for Logistics, as the queue counts it
    extractionToReview: z.boolean().optional().catch(undefined),
    // ADR-139: received with an outcome Logistics has not confirmed, as the queue counts it
    toResolve: z.boolean().optional().catch(undefined),
    // ADR-140: missing units Logistics confirmed, waiting for the supplier's choice
    toSettle: z.boolean().optional().catch(undefined),
    // B-41, B-42, ADR-245: what the supplier left undone after the last reminder, Logistics' queue
    remindersOver: z.boolean().optional().catch(undefined),
    // the queues of a side open the list with it: for Tomorrow it takes nothing out (ADR-170)
    asSupplier: z.boolean().optional().catch(undefined),
    asBrand: z.boolean().optional().catch(undefined),
    // one brand or supplier, as the Activity of its organization opens its inbounds (ADR-322)
    brand: listInboundsInput.shape.brand.catch(undefined),
    supplier: listInboundsInput.shape.supplier.catch(undefined),
    q: listInboundsInput.shape.q.catch(undefined),
    ...todoSearch,
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: async ({ context, deps: { todo, ...filters } }) => {
    if (!todo) await context.queryClient.ensureInfiniteQueryData(inboundQueries.list(filters));
  },
  component: lazyRouteComponent(() => import("./features/inbound/inbounds-page"), "InboundsPage"),
});

const inboundRoute = createRoute({
  getParentRoute: () => shellRoute,
  // by id or by number
  path: "/inbounds/$inboundKey",
  validateSearch: z.object({
    tab: z.enum(inboundTabs).optional().catch(undefined),
    // the panel of the assistant (ADR-284)
    ai: z.boolean().optional().catch(undefined),
  }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(inboundQueries.detail(params.inboundKey)),
  component: lazyRouteComponent(() => import("./features/inbound/inbound-page"), "InboundPage"),
});

const warehouseReceiptsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/warehouse-receipts",
  validateSearch: z.object({ reason: listWaitingReceiptsInput.shape.reason.catch(undefined) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(receiptQueries.waiting(deps)),
  component: lazyRouteComponent(() => import("./features/receipt/warehouse-receipts-page"), "WarehouseReceiptsPage"),
});

const stockMovementsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/stock-movements",
  // the transfer orders Operations confirms are the status filter, as the queue of the home counts them
  validateSearch: z.object({
    status: listStockMovementsInput.shape.status.catch(undefined),
    type: listStockMovementsInput.shape.type.catch(undefined),
    location: listStockMovementsInput.shape.location.catch(undefined),
    q: listStockMovementsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(stockMovementQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/stock-movement/stock-movements-page"), "StockMovementsPage"),
});

const newStockMovementRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/stock-movements/new",
  // the warehouses the movement leaves and reaches, with their company
  loader: ({ context }) => context.queryClient.ensureQueryData(salesOrderQueries.options()),
  component: lazyRouteComponent(
    () => import("./features/stock-movement/new-stock-movement-page"),
    "NewStockMovementPage",
  ),
});

const stockMovementRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/stock-movements/$movementId",
  validateSearch: z.object({ tab: z.enum(stockMovementTabs).optional().catch(undefined) }),
  // ADR-226, ADR-229: an adjustment and a vendor return are movements too, and the error queue and the gates name them
  // as one: a vendor return opens on its page (the systems tab kept), an adjustment in its list
  loaderDeps: ({ search: { tab } }) => ({ tab }),
  loader: async ({ context, params, deps }) => {
    try {
      return await context.queryClient.ensureQueryData(stockMovementQueries.detail(params.movementId));
    } catch (error) {
      if (!(error instanceof ApiRequestError && error.status === 404)) throw error;
      const vendorReturn = await context.queryClient
        .ensureQueryData(vendorReturnQueries.detail(params.movementId))
        .catch(() => null);
      if (vendorReturn) {
        throw redirect({
          to: "/vendor-returns/$vendorReturnId",
          params: { vendorReturnId: vendorReturn.id },
          search: { tab: deps.tab === "systems" ? "systems" : undefined },
        });
      }
      throw redirect({ to: "/stock-adjustments", search: { panel: params.movementId } });
    }
  },
  component: lazyRouteComponent(() => import("./features/stock-movement/stock-movement-page"), "StockMovementPage"),
});

const stockAdjustmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/stock-adjustments",
  // the adjustments Logistics confirms and Finance approves are the status filter, as the queues of the home count them
  validateSearch: z.object({
    status: listStockAdjustmentsInput.shape.status.catch(undefined),
    location: listStockAdjustmentsInput.shape.location.catch(undefined),
    q: listStockAdjustmentsInput.shape.q.catch(undefined),
    ...todoSearch,
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: async ({ context, deps: { todo, ...filters } }) => {
    if (!todo) await context.queryClient.ensureInfiniteQueryData(stockAdjustmentQueries.list(filters));
  },
  component: lazyRouteComponent(
    () => import("./features/stock-adjustment/stock-adjustments-page"),
    "StockAdjustmentsPage",
  ),
});

const vendorReturnsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/vendor-returns",
  // the returns the supplier answers and the ones it refused are the status filter, as the queues of the home count them
  validateSearch: z.object({
    status: listVendorReturnsInput.shape.status.catch(undefined),
    location: listVendorReturnsInput.shape.location.catch(undefined),
    supplier: listVendorReturnsInput.shape.supplier.catch(undefined),
    q: listVendorReturnsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(vendorReturnQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/vendor-return/vendor-returns-page"), "VendorReturnsPage"),
});

const newVendorReturnRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/vendor-returns/new",
  // the warehouses the units leave, with their company
  loader: ({ context }) => context.queryClient.ensureQueryData(salesOrderQueries.options()),
  component: lazyRouteComponent(() => import("./features/vendor-return/new-vendor-return-page"), "NewVendorReturnPage"),
});

const vendorReturnRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/vendor-returns/$vendorReturnId",
  validateSearch: z.object({ tab: z.enum(vendorReturnTabs).optional().catch(undefined) }),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(vendorReturnQueries.detail(params.vendorReturnId)),
  component: lazyRouteComponent(() => import("./features/vendor-return/vendor-return-page"), "VendorReturnPage"),
});

const warehouseCountsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/warehouse-counts",
  validateSearch: z.object({ reason: listWaitingDifferencesInput.shape.reason.catch(undefined) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(stockAdjustmentQueries.waiting(deps)),
  component: lazyRouteComponent(
    () => import("./features/stock-adjustment/warehouse-counts-page"),
    "WarehouseCountsPage",
  ),
});

const warehouseExecutionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/warehouse-executions",
  validateSearch: z.object({ reason: listWaitingMovementExecutionsInput.shape.reason.catch(undefined) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(movementExecutionQueries.waiting(deps)),
  component: lazyRouteComponent(
    () => import("./features/movement-execution/warehouse-executions-page"),
    "WarehouseExecutionsPage",
  ),
});

const stockAvailabilityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/stock-availability",
  validateSearch: z.object({
    location: listAvailabilityInput.shape.location.catch(undefined),
    q: listAvailabilityInput.shape.q.catch(undefined),
    // the order of the domain when absent
    sort: listAvailabilityInput.shape.sort.unwrap().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(stockAvailabilityQueries.list(deps)),
  component: lazyRouteComponent(
    () => import("./features/stock-availability/stock-availability-page"),
    "StockAvailabilityPage",
  ),
});

const availabilityWaitingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/availability-waiting",
  validateSearch: z.object({ reason: listWaitingAvailabilityInput.shape.reason.catch(undefined) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(stockAvailabilityQueries.waiting(deps)),
  component: lazyRouteComponent(
    () => import("./features/stock-availability/waiting-availability-page"),
    "WaitingAvailabilityPage",
  ),
});

const availabilityMismatchesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/availability-mismatches",
  validateSearch: z.object({
    location: listAvailabilityMismatchesInput.shape.location.catch(undefined),
    // open when absent, as the domain
    state: listAvailabilityMismatchesInput.shape.state.unwrap().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(stockAvailabilityQueries.mismatches(deps)),
  component: lazyRouteComponent(
    () => import("./features/stock-availability/availability-mismatches-page"),
    "AvailabilityMismatchesPage",
  ),
});

const warehouseShipmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/warehouse-shipments",
  validateSearch: z.object({ reason: listWaitingShipmentsInput.shape.reason.catch(undefined) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(shipmentQueries.waiting(deps)),
  component: lazyRouteComponent(() => import("./features/shipment/warehouse-shipments-page"), "WarehouseShipmentsPage"),
});

const fulfillmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/fulfillments",
  validateSearch: z.object({
    status: listFulfillmentsInput.shape.status.catch(undefined),
    location: listFulfillmentsInput.shape.location.catch(undefined),
    syncStatus: listFulfillmentsInput.shape.syncStatus.catch(undefined),
    // to book and late are not statuses (ADR-152): the status filter offers them, as the queues of the home count them
    toBook: z.literal(true).optional().catch(undefined),
    ackOverdue: z.literal(true).optional().catch(undefined),
    q: listFulfillmentsInput.shape.q.catch(undefined),
    ...todoSearch,
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: async ({ context, deps: { todo, ...filters } }) => {
    if (!todo) await context.queryClient.ensureInfiniteQueryData(fulfillmentQueries.list(filters));
  },
  component: lazyRouteComponent(() => import("./features/fulfillment/fulfillments-page"), "FulfillmentsPage"),
});

const fulfillmentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/fulfillments/$fulfillmentId",
  validateSearch: z.object({ tab: z.enum(fulfillmentTabs).optional().catch(undefined) }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(fulfillmentQueries.detail(params.fulfillmentId)),
  component: lazyRouteComponent(() => import("./features/fulfillment/fulfillment-page"), "FulfillmentPage"),
});

const creditNotesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/credit-notes",
  // ADR-140: the two queues of the home and the statuses, as the list counts them
  validateSearch: z.object({
    status: listCreditNotesInput.shape.status.catch(undefined),
    toReview: z.literal(true).optional().catch(undefined),
    toSend: z.literal(true).optional().catch(undefined),
    // B-41, ADR-245: the file still owed after the last reminder, Finance's queue
    remindersOver: z.literal(true).optional().catch(undefined),
    // the supplier's side of the queues: for Tomorrow it takes nothing out (ADR-170)
    asSupplier: z.boolean().optional().catch(undefined),
    ...todoSearch,
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps: { todo, ...filters } }) => {
    if (!todo) await context.queryClient.ensureInfiniteQueryData(creditNoteQueries.list(filters));
  },
  component: lazyRouteComponent(() => import("./features/credit-note/credit-notes-page"), "CreditNotesPage"),
});

const salesOrdersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/sales-orders",
  validateSearch: z.object({
    status: listSalesOrdersInput.shape.status.catch(undefined),
    // ADR-261: one customer, as a row of the orders by season and status opens its orders
    customer: listSalesOrdersInput.shape.customer.catch(undefined),
    brand: listSalesOrdersInput.shape.brand.catch(undefined),
    season: listSalesOrdersInput.shape.season.catch(undefined),
    bcSyncStatus: listSalesOrdersInput.shape.bcSyncStatus.catch(undefined),
    // the lists of v0.2 §9 and the changes of Joor that wait, as the status filter offers them
    joorChangePending: z.boolean().optional().catch(undefined),
    lockable: z.boolean().optional().catch(undefined),
    creditBlocked: z.boolean().optional().catch(undefined),
    awaitingPayment: z.boolean().optional().catch(undefined),
    depositDue: z.boolean().optional().catch(undefined),
    fulfillmentIssue: z.boolean().optional().catch(undefined),
    uncovered: z.boolean().optional().catch(undefined),
    dunningOver: z.boolean().optional().catch(undefined),
    // C-56, ADR-259: an order the allocation still serves without a season ranks as of no active season
    withoutSeason: z.boolean().optional().catch(undefined),
    // C-45, ADR-261: an open order no confirmation was issued or asked for, a queue of the Customer Service
    withoutConfirmation: z.boolean().optional().catch(undefined),
    // B-47, ADR-208: where the latest revision of the confirmation stands with the customer, two queues of the home
    confirmation: listSalesOrdersInput.shape.confirmation.catch(undefined),
    q: listSalesOrdersInput.shape.q.catch(undefined),
    ...todoSearch,
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps: { todo, ...filters } }) =>
    Promise.all([
      todo || context.queryClient.ensureInfiniteQueryData(salesOrderQueries.list(filters)),
      context.queryClient.ensureQueryData(salesOrderQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/sales-order/sales-orders-page"), "SalesOrdersPage"),
});

const salesOrderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/sales-orders/$salesOrderId",
  validateSearch: z.object({
    tab: z.enum(salesOrderTabs).optional().catch(undefined),
    // which lines the grid shows
    show: z.enum(soLineStatuses).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ status: search.show }),
  loader: async ({ context, params, deps }) => {
    const so = await context.queryClient.ensureQueryData(salesOrderQueries.detail(params.salesOrderId));
    // the lines are read by id, the route also answers to a number
    await context.queryClient.ensureInfiniteQueryData(soLineQueries.list(so.id, deps.status));
  },
  component: lazyRouteComponent(() => import("./features/sales-order/sales-order-page"), "SalesOrderPage"),
});

const allocationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/allocation",
  validateSearch: z.object({
    // the first warehouse of the options when none is chosen
    warehouse: z.uuid().optional().catch(undefined),
    readiness: listAllocationOrdersInput.shape.readiness.catch(undefined),
    excluded: z.boolean().optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(salesOrderQueries.options()),
  component: lazyRouteComponent(() => import("./features/allocation/allocation-page"), "AllocationPage"),
});

const orderConfirmationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/order-confirmations",
  validateSearch: z.object({
    brand: z.uuid().optional().catch(undefined),
    season: z.uuid().optional().catch(undefined),
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(salesOrderQueries.options()),
  component: lazyRouteComponent(
    () => import("./features/order-confirmation/order-confirmations-page"),
    "OrderConfirmationsPage",
  ),
});

const joorOrdersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/joor-orders",
  validateSearch: z.object({
    // the orders that wait unless `all`
    all: z.boolean().optional().catch(undefined),
    reason: listJoorOrdersInput.shape.reason.catch(undefined),
    q: listJoorOrdersInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { all, reason, q } }) => ({ waiting: !all, reason, q }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(joorOrderQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/joor-order/joor-orders-page"), "JoorOrdersPage"),
});

const creditDecisionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/credit-decisions",
  validateSearch: z.object({ customer: listCreditDecisionsInput.shape.customer.catch(undefined) }),
  loaderDeps: ({ search }) => (search.customer ? { customer: search.customer } : {}),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(creditQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/credit/credit-decisions-page"), "CreditDecisionsPage"),
});

// spec v0.2 §9, P8.10a: Credit Control's view of the customers whose credit holds their orders back
const blockedCustomersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/blocked-customers",
  validateSearch: z.object({
    cause: listBlockedCustomersInput.shape.cause.catch(undefined),
    q: listBlockedCustomersInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(blockedCustomerQueries.list(deps)),
  component: lazyRouteComponent(
    () => import("./features/blocked-customer/blocked-customers-page"),
    "BlockedCustomersPage",
  ),
});

// spec v0.3 §9, ADR-261: the orders counted by season, brand, customer and status
const salesOrderTotalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/sales-order-totals",
  validateSearch: z.object({
    season: listSalesOrderTotalsInput.shape.season.catch(undefined),
    brand: listSalesOrderTotalsInput.shape.brand.catch(undefined),
    status: listSalesOrderTotalsInput.shape.status.catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(salesOrderTotalQueries.list(deps)),
      context.queryClient.ensureQueryData(salesOrderQueries.options()),
    ]),
  component: lazyRouteComponent(
    () => import("./features/sales-order-total/sales-order-totals-page"),
    "SalesOrderTotalsPage",
  ),
});

// spec v0.3 §9, C-55, ADR-261: the cash the open orders still bring, a period open next to the table
const cashProjectionRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/cash-projection",
  validateSearch: z.object({
    legalEntity: getCashProjectionInput.shape.legalEntity.catch(undefined),
    brand: getCashProjectionInput.shape.brand.catch(undefined),
    panel: z
      .enum([...cashProjectionBuckets, ...cashProjectionExclusions])
      .optional()
      .catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(cashProjectionQueries.get(deps)),
      context.queryClient.ensureQueryData(salesOrderQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/cash-projection/cash-projection-page"), "CashProjectionPage"),
});

const hilldunApprovalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/hilldun-approvals",
  component: lazyRouteComponent(() => import("./features/credit/hilldun-approvals-page"), "HilldunApprovalsPage"),
});

const documentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/documents",
  validateSearch: z.object({
    status: listDocumentsInput.shape.status.catch(undefined),
    type: listDocumentsInput.shape.type.catch(undefined),
    // late and "to ask again" are not statuses (ADR-118, ADR-127): the status filter offers them, as the queues count them
    overdue: z.literal(true).optional().catch(undefined),
    retryable: z.literal(true).optional().catch(undefined),
    // one customer, as the Activity of its organization opens its documents (ADR-322)
    customer: listDocumentsInput.shape.customer.catch(undefined),
    q: listDocumentsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(documentQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/document/documents-page"), "DocumentsPage"),
});

// The claims of the customers (FR-M15-04, ADR-228): the history by customer, SKU, type, state, outcome and order, in
// the URL; the claim next to the list.
const claimsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/claims",
  validateSearch: z.object({
    type: listClaimsInput.shape.type.catch(undefined),
    status: listClaimsInput.shape.status.catch(undefined),
    outcome: listClaimsInput.shape.outcome.catch(undefined),
    customer: listClaimsInput.shape.customer.catch(undefined),
    sku: listClaimsInput.shape.sku.catch(undefined),
    salesOrder: listClaimsInput.shape.salesOrder.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(claimQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/claim/claims-page"), "ClaimsPage"),
});

const shortShipmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/short-shipments",
  validateSearch: z.object({
    // the queue of the home is the ones not credited yet (ADR-199)
    credited: z.boolean().optional().catch(undefined),
    q: listShortShipmentsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(shortShipmentQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/short-shipment/short-shipments-page"), "ShortShipmentsPage"),
});

const integrationErrorsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/integration",
  validateSearch: z.object({
    problem: listIntegrationErrorsInput.shape.problem.catch(undefined),
    kind: listIntegrationErrorsInput.shape.kind.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ problem: search.problem, kind: search.kind }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(integrationQueries.list(deps)),
  component: lazyRouteComponent(
    () => import("./features/integration/integration-errors-page"),
    "IntegrationErrorsPage",
  ),
});

// the files to the ERP waiting to be imported by hand, the oldest first (spec v0.3 §7.5, ADR-258): the page of the role
// "ERP import"
const erpFilesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/erp-files",
  validateSearch: z.object({
    status: z
      .enum([...erpFileStatuses, "all"])
      .optional()
      .catch(undefined),
    kind: z.enum(erpFileKinds).optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ status: search.status ?? "to_import", kind: search.kind }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(erpFileQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/erp-file/erp-files-page"), "ErpFilesPage"),
});

// what the hourly check finds wrong towards Business Central (FR-M16-03, ADR-177), next to the integration errors
const reconciliationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/reconciliation",
  validateSearch: z.object({
    kind: listReconciliationIssuesInput.shape.kind.catch(undefined),
    entity: listReconciliationIssuesInput.shape.entityType.catch(undefined),
    state: z.literal("resolved").optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ kind: search.kind, entityType: search.entity, state: search.state }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(reconciliationQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/integration/reconciliation-page"), "ReconciliationPage"),
});

// the log of the emails (ADR-149 point 9), next to the integration errors
const notificationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/notifications",
  validateSearch: z.object({
    status: listNotificationsInput.shape.status.catch(undefined),
    templateCode: listNotificationsInput.shape.templateCode.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ status: search.status, templateCode: search.templateCode }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(notificationQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/notification/notifications-page"), "NotificationsPage"),
});

const referenceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/reference",
  validateSearch: z.object({
    dataset: listReferenceRecordsInput.shape.dataset.optional().catch(undefined),
    q: listReferenceRecordsInput.shape.q.catch(undefined),
    // the record open in the panel (a uuid, or the code of a natural key), or the form of a new one
    panel: z.string().min(1).max(40).optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ dataset: search.dataset ?? DEFAULT_DATASET, q: search.q }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(referenceQueries.list(deps.dataset, deps.q)),
      // the names of the records the cells point to
      ...referencedDatasets(deps.dataset).map((key) =>
        context.queryClient.ensureQueryData(referenceQueries.whole(key)),
      ),
    ]),
  component: lazyRouteComponent(() => import("./features/reference/reference-page"), "ReferencePage"),
});

const parametersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/parameters",
  // the parameter open in the panel
  validateSearch: z.object({ panel: z.enum(parameterKeys).optional().catch(undefined) }),
  loader: ({ context }) => context.queryClient.ensureQueryData(parameterQueries.list()),
  component: lazyRouteComponent(() => import("./features/parameter/parameters-page"), "ParametersPage"),
});

const gatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/gates",
  // the gate open in the panel
  validateSearch: z.object({ panel: z.uuid().optional().catch(undefined) }),
  loader: ({ context }) => context.queryClient.ensureQueryData(gateQueries.list()),
  component: lazyRouteComponent(() => import("./features/gate/gates-page"), "GatesPage"),
});

// B-60, ADR-224: what waits for an approval in every domain, the status filter as the queue of the home counts it
const waitingGatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/approvals",
  validateSearch: z.object({ status: listWaitingGatesInput.shape.status.catch(undefined) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(waitingGateQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/gate/waiting-gates-page"), "WaitingGatesPage"),
});

const notificationTemplatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/notification-templates",
  // the template open in the panel
  validateSearch: z.object({ panel: z.uuid().optional().catch(undefined) }),
  loader: ({ context }) => context.queryClient.ensureQueryData(templateQueries.list()),
  component: lazyRouteComponent(() => import("./features/notification-template/templates-page"), "TemplatesPage"),
});

const recipientListsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/recipient-lists",
  // the list open in the panel
  validateSearch: z.object({ panel: z.uuid().optional().catch(undefined) }),
  loader: ({ context }) => context.queryClient.ensureQueryData(recipientListQueries.list()),
  component: lazyRouteComponent(() => import("./features/recipient-list/recipient-lists-page"), "RecipientListsPage"),
});

const emailSuppressionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/email-suppressions",
  validateSearch: z.object({
    q: listEmailSuppressionsInput.shape.q.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ q: search.q }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(suppressionQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/email-suppression/suppressions-page"), "SuppressionsPage"),
});

const toleranceBandsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/tolerance-bands",
  // the set open in the panel: the global one, or a brand's by its id (ADR-135)
  validateSearch: z.object({
    panel: z
      .union([z.literal("global"), z.uuid()])
      .optional()
      .catch(undefined),
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(toleranceBandQueries.list()),
  component: lazyRouteComponent(() => import("./features/tolerance-band/tolerance-bands-page"), "ToleranceBandsPage"),
});

const allocationSchedulesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/allocation-schedules",
  // the warehouse open in the panel (ADR-186), or the ranking (ADR-194)
  validateSearch: z.object({
    panel: z
      .union([z.uuid(), z.literal("ranking")])
      .optional()
      .catch(undefined),
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(allocationScheduleQueries.list()),
      context.queryClient.ensureQueryData(allocationSettingQueries.get()),
    ]),
  component: lazyRouteComponent(
    () => import("./features/allocation-schedule/allocation-schedules-page"),
    "AllocationSchedulesPage",
  ),
});

const organizationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organizations",
  validateSearch: z.object({
    q: listOrganizationsInput.shape.q.catch(undefined),
    kind: listOrganizationsInput.shape.kind.catch(undefined),
    bcSyncStatus: listOrganizationsInput.shape.bcSyncStatus.catch(undefined),
    withoutOrderContact: z.literal(true).optional().catch(undefined),
    toRegisterOnBc: z.literal(true).optional().catch(undefined),
    archived: z.boolean().optional().catch(undefined),
    ...todoSearch,
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({
    q: search.q,
    kind: search.kind,
    bcSyncStatus: search.bcSyncStatus,
    withoutOrderContact: search.withoutOrderContact,
    toRegisterOnBc: search.toRegisterOnBc,
    archived: search.archived,
    todo: search.todo,
  }),
  loader: async ({ context, deps: { todo, ...filters } }) => {
    if (!todo) await context.queryClient.ensureInfiniteQueryData(organizationQueries.list(filters));
  },
  component: lazyRouteComponent(() => import("./features/organization/organizations-page"), "OrganizationsPage"),
});

// before the page of one organization: a static segment wins over the id
const organizationDuplicatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organizations/duplicates",
  loader: ({ context }) => context.queryClient.ensureInfiniteQueryData(organizationQueries.duplicates()),
  component: lazyRouteComponent(() => import("./features/organization/duplicates-page"), "DuplicatesPage"),
});

const organizationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organizations/$organizationId",
  validateSearch: z.object({ tab: z.enum(organizationTabs).optional().catch(undefined) }),
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(organizationQueries.detail(params.organizationId)),
      context.queryClient.ensureQueryData(organizationQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/organization/organization-page"), "OrganizationPage"),
});

const etafApplicationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/etaf",
  validateSearch: z.object({
    q: listEtafApplicationsInput.shape.q.catch(undefined),
    view: z.enum(etafViews).optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ q: search.q, view: search.view }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(etafQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/etaf/etaf-applications-page"), "EtafApplicationsPage"),
});

const etafApplicationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/etaf/$applicationId",
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(etafQueries.detail(params.applicationId)),
      context.queryClient.ensureQueryData(organizationQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/etaf/etaf-application-page"), "EtafApplicationPage"),
});

const manufacturersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/manufacturers",
  validateSearch: z.object({
    q: listManufacturersInput.shape.q.catch(undefined),
    supplier: listManufacturersInput.shape.supplierOrgId.catch(undefined),
    archived: z.boolean().optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ q: search.q, supplierOrgId: search.supplier, archived: search.archived }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(manufacturerQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/manufacturer/manufacturers-page"), "ManufacturersPage"),
});

const areaManagersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/area-managers",
  // the search runs in the page, on every country; the country open in the panel by its code (ADR-246)
  validateSearch: z.object({
    q: z.string().trim().min(1).max(100).optional().catch(undefined),
    panel: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .optional()
      .catch(undefined),
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(countryAreaManagerQueries.list()),
  component: lazyRouteComponent(
    () => import("./features/country-area-manager/country-area-managers-page"),
    "CountryAreaManagersPage",
  ),
});

const bcRecordsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/bc-records",
  validateSearch: z.object({
    q: listBcRecordsInput.shape.q.catch(undefined),
    kind: z.enum(bcRecordKinds).optional().catch(undefined),
    all: z.literal(true).optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ q: search.q, kind: search.kind, waiting: !search.all || undefined }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(bcRecordQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/bc-record/bc-records-page"), "BcRecordsPage"),
});

const hubspotRecordsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/hubspot-records",
  validateSearch: z.object({
    q: listHubspotRecordsInput.shape.q.catch(undefined),
    kind: z.enum(hubspotRecordKinds).optional().catch(undefined),
    reason: z.enum(hubspotWaitingReasons).optional().catch(undefined),
    all: z.literal(true).optional().catch(undefined),
    archived: z.literal(true).optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({
    q: search.q,
    kind: search.kind,
    waitingReason: search.reason,
    waiting: !search.all || undefined,
    archived: search.archived,
  }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(hubspotRecordQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/hubspot-record/hubspot-records-page"), "HubspotRecordsPage"),
});

const buyingGroupsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/buying-groups",
  validateSearch: z.object({
    q: listBuyingGroupsInput.shape.q.catch(undefined),
    archived: z.boolean().optional().catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ q: search.q, archived: search.archived }),
  loader: ({ context, deps }) => context.queryClient.ensureInfiniteQueryData(buyingGroupQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/buying-group/buying-groups-page"), "BuyingGroupsPage"),
});

const usersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/users",
  validateSearch: z.object({
    q: listUsersInput.shape.q.catch(undefined),
    orgType: listUsersInput.shape.orgType.catch(undefined),
    status: listUsersInput.shape.status.catch(undefined),
    role: listUsersInput.shape.role.catch(undefined),
    // B-88, ADR-245: the invitations not accepted after the reminders, the queue of who sent them
    invitationRemindersOver: listUsersInput.shape.invitationRemindersOver.catch(undefined),
    ...todoSearch,
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...filters } }) => filters,
  loader: ({ context, deps: { todo, ...filters } }) =>
    Promise.all([
      todo || context.queryClient.ensureInfiniteQueryData(userQueries.list(filters)),
      context.queryClient.ensureQueryData(userQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/user/users-page"), "UsersPage"),
});

const rolesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/roles",
  loader: ({ context }) => context.queryClient.ensureQueryData(roleQueries.list()),
  component: lazyRouteComponent(() => import("./features/role/roles-page"), "RolesPage"),
});

const roleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/roles/$roleId",
  validateSearch: z.object({ tab: z.enum(roleTabs).optional().catch(undefined) }),
  // the roles come all at once: the page takes its own from them
  loader: ({ context }) => context.queryClient.ensureQueryData(roleQueries.list()),
  component: lazyRouteComponent(() => import("./features/role/role-page"), "RolePage"),
});

const codeMappingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/code-mappings",
  validateSearch: z.object({
    system: listCodeMappingsInput.shape.system.optional().catch(undefined),
    domain: listCodeMappingsInput.shape.domain.optional().catch(undefined),
    brand: listCodeMappingsInput.shape.brandOrgId.catch(undefined),
    unmapped: z.boolean().optional().catch(undefined),
    q: listCodeMappingsInput.shape.q.catch(undefined),
  }),
  loaderDeps: ({ search }) => ({
    system: search.system ?? DEFAULT_SYSTEM,
    domain: search.domain ?? DEFAULT_DOMAIN,
    brandOrgId: search.brand,
    unmapped: search.unmapped,
    q: search.q,
  }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(codeMappingQueries.list(deps)),
      context.queryClient.ensureQueryData(codeMappingQueries.summary(deps)),
    ]),
  component: lazyRouteComponent(() => import("./features/code-mapping/code-mappings-page"), "CodeMappingsPage"),
});

// the emails and the pushes the signed-in user turned off (ADR-149 point 7, ADR-283): every user, from the user menu and ⌘K
const notificationPreferencesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings/notifications",
  loader: ({ context }) => context.queryClient.ensureQueryData(preferenceQueries.mine()),
  component: lazyRouteComponent(() => import("./features/notification-preference/preferences-page"), "PreferencesPage"),
});

// the personal tokens of the signed-in user (ADR-262): every user of Tomorrow, from the user menu
const myTokensRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: myTokensPath,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(delegatedAccessQueries.mine()),
      context.queryClient.ensureQueryData(delegatedAccessQueries.options()),
    ]),
  component: lazyRouteComponent(() => import("./features/delegated-access/my-tokens-page"), "MyTokensPage"),
});

// how to connect Claude, with the address of the MCP server (ADR-263): every user of Tomorrow, from the user menu
const connectClaudeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: connectClaudePath,
  loader: ({ context }) => context.queryClient.ensureQueryData(delegatedAccessQueries.options()),
  component: lazyRouteComponent(() => import("./features/delegated-access/connect-claude-page"), "ConnectClaudePage"),
});

// what Claude or a token prepared for the signed-in user to confirm (ADR-265): every user of Tomorrow, from the user
// menu and from the link the tool answers
const preparedActionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: preparedActionsPath,
  loader: ({ context }) => context.queryClient.ensureQueryData(preparedActionQueries.list("open")),
  component: lazyRouteComponent(
    () => import("./features/prepared-action/prepared-actions-page"),
    "PreparedActionsPage",
  ),
});

const preparedActionRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: `${preparedActionsPath}/$actionId`,
  loader: ({ context, params }) => context.queryClient.ensureQueryData(preparedActionQueries.detail(params.actionId)),
  component: lazyRouteComponent(() => import("./features/prepared-action/prepared-action-page"), "PreparedActionPage"),
});

// every token and connection of every user, for the administrator (ADR-262)
const delegatedAccessRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/admin/delegated-access",
  validateSearch: z.object({
    status: listDelegatedAccessInput.shape.status.catch(undefined),
    kind: listDelegatedAccessInput.shape.kind.catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(delegatedAccessQueries.list(deps)),
      context.queryClient.ensureQueryData(delegatedAccessQueries.clients()),
      context.queryClient.ensureQueryData(delegatedAccessQueries.blocks()),
      context.queryClient.ensureQueryData(delegatedAccessQueries.options()),
    ]),
  component: lazyRouteComponent(
    () => import("./features/delegated-access/delegated-access-page"),
    "DelegatedAccessPage",
  ),
});

// Kitchen sink (ADR-038): the flag is a build-time constant, so the production bundle does not even contain it.
const kitchenSinkRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/_kitchen-sink",
  beforeLoad: () => {
    if (!import.meta.env.NEXXT_DEV_TOOLS) throw notFound();
  },
  component: import.meta.env.NEXXT_DEV_TOOLS
    ? lazyRouteComponent(() => import("./features/kitchen-sink/kitchen-sink-page"), "KitchenSinkPage")
    : () => null,
});

// The simulator of TIL (ADR-266), as the kitchen sink: only where the mock of TIL is, never in the production bundle.
const tilSimulatorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/_til-simulator",
  beforeLoad: () => {
    if (!import.meta.env.NEXXT_DEV_TOOLS) throw notFound();
  },
  component: import.meta.env.NEXXT_DEV_TOOLS
    ? lazyRouteComponent(() => import("./features/til-simulator/til-simulator-page"), "TilSimulatorPage")
    : () => null,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  shellRoute.addChildren([
    homeRoute,
    searchRoute,
    linesheetsRoute,
    linesheetRoute,
    productsRoute,
    priceSetupRoute,
    marginsRoute,
    attributeTasksRoute,
    attributeCompletenessRoute,
    seasonCalendarRoute,
    scorecardRoute,
    seasonBlockersRoute,
    attributeTaskRoute,
    invitationsRoute,
    salesCampaignsRoute,
    salesCampaignRoute,
    appointmentsRoute,
    purchaseOrdersRoute,
    purchaseOrderRoute,
    demandRoute,
    supplierTotalsRoute,
    inboundProgressRoute,
    inboundsRoute,
    inboundRoute,
    warehouseReceiptsRoute,
    creditNotesRoute,
    stockMovementsRoute,
    newStockMovementRoute,
    stockMovementRoute,
    stockAdjustmentsRoute,
    vendorReturnsRoute,
    newVendorReturnRoute,
    vendorReturnRoute,
    warehouseCountsRoute,
    warehouseExecutionsRoute,
    stockAvailabilityRoute,
    availabilityWaitingRoute,
    availabilityMismatchesRoute,
    warehouseShipmentsRoute,
    fulfillmentsRoute,
    fulfillmentRoute,
    salesOrdersRoute,
    salesOrderRoute,
    allocationRoute,
    orderConfirmationsRoute,
    joorOrdersRoute,
    creditDecisionsRoute,
    blockedCustomersRoute,
    salesOrderTotalsRoute,
    cashProjectionRoute,
    hilldunApprovalsRoute,
    documentsRoute,
    claimsRoute,
    shortShipmentsRoute,
    organizationsRoute,
    organizationDuplicatesRoute,
    organizationRoute,
    manufacturersRoute,
    areaManagersRoute,
    bcRecordsRoute,
    hubspotRecordsRoute,
    buyingGroupsRoute,
    etafApplicationsRoute,
    etafApplicationRoute,
    integrationErrorsRoute,
    reconciliationRoute,
    erpFilesRoute,
    notificationsRoute,
    referenceRoute,
    parametersRoute,
    gatesRoute,
    waitingGatesRoute,
    notificationTemplatesRoute,
    recipientListsRoute,
    emailSuppressionsRoute,
    toleranceBandsRoute,
    allocationSchedulesRoute,
    codeMappingsRoute,
    usersRoute,
    rolesRoute,
    roleRoute,
    delegatedAccessRoute,
    notificationPreferencesRoute,
    myTokensRoute,
    connectClaudeRoute,
    preparedActionsRoute,
    preparedActionRoute,
    kitchenSinkRoute,
    tilSimulatorRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // the query cache decides freshness, not the router
  defaultPreloadStaleTime: 0,
  defaultPendingMs: 300,
  defaultViewTransition: true,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
